import { useCallback, useRef, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronRight, ChevronLeft, Plus, Sun, Moon, Monitor, Eye } from 'lucide-react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useIsViewer, useIsArchivedHousehold } from '@/hooks/useCurrentHousehold'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { useDueRecurringCount } from '@/hooks/useDueRecurringCount'
import { useEntitlement } from '@/hooks/useEntitlement'
import { SampleWatermark } from '@/components/sample/SampleWatermark'
import { TrialBanner } from '@/components/billing/TrialBanner'
import { TrialEndedBanner } from '@/components/billing/TrialEndedBanner'
import { useTheme, type ThemePreference } from '@/hooks/useTheme'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/Logo'
import { AccountMenu } from '@/components/AccountMenu'
import { signOut } from '@/api/users'
import { signOutAndReset } from '@/lib/signOut'
import { useClerk } from '@clerk/clerk-react'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'
import { listAccounts as listAccountsApi } from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { AddTransactionModal } from '@/components/transactions/TransactionForms'
import { NAV_ITEMS } from './navItems'
import { NavBadge } from './NavBadge'
import { MobileTabBar } from './MobileTabBar'
import { MobileMenuSheet } from './MobileMenuSheet'
import { PullToRefreshIndicator, REFRESHING_GAP } from './PullToRefreshIndicator'

const THEME_CYCLE: ThemePreference[] = ['light', 'dark', 'system']
const THEME_ICONS: Record<ThemePreference, typeof Sun> = { light: Sun, dark: Moon, system: Monitor }
const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

/** Flags why the active household is read-only: a VIEWER role, the household itself being
 *  archived, or a lapsed trial (both read-only for every role, owner included). Archived and
 *  trial-ended take priority in the wording since they explain *why* even an owner can't edit
 *  right now. Takes its
 *  own className (rather than a wrapping div at the call site) so callers don't end up with a
 *  phantom margin box when this renders null. */
function ReadOnlyBadge({ className = '' }: { className?: string }) {
  const isViewer = useIsViewer()
  const isArchivedHousehold = useIsArchivedHousehold()
  const { data: entitlement } = useEntitlement()
  const billingLapsed = entitlement !== undefined && !entitlement.entitled
  if (!isViewer && !isArchivedHousehold && !billingLapsed) return null

  return (
    <span
      className={`flex items-center gap-1.5 rounded-md bg-[var(--fl-warning-bg)] px-2.5 py-1 text-xs font-medium text-[var(--fl-warning-text)] ${className}`}
    >
      <Eye size={12} />{' '}
      {isArchivedHousehold
        ? 'Archived — read-only'
        : billingLapsed
          ? 'Trial ended — read-only'
          : 'Read-only access'}
    </span>
  )
}

export function AppLayout() {
  const { data: user } = useCurrentUser()
  const { data: entitlement } = useEntitlement()
  const isReadOnly = useIsReadOnly()
  // A lapsed household's data calls return 402, so there's nothing to count.
  const dueCount = useDueRecurringCount(entitlement?.entitled === true)
  const navigate = useNavigate()
  const clerk = useClerk()
  const { preference: themePreference, setPreference: setThemePreference } = useTheme()

  const [menuOpen, setMenuOpen] = useState(false)
  const [sidebarExpanded, setSidebarExpanded] = useState(true)
  const [addTransactionOpen, setAddTransactionOpen] = useState(false)

  const queryClient = useQueryClient()
  const mainRef = useRef<HTMLElement>(null)
  const refreshScreen = useCallback(async () => {
    try {
      await queryClient.refetchQueries({ type: 'active' }, { throwOnError: true })
    } catch {
      toast.error('Couldn’t refresh — check your connection.')
    }
  }, [queryClient])
  const { pull, refreshing } = usePullToRefresh(mainRef, refreshScreen)

  function cycleTheme() {
    const next = THEME_CYCLE[(THEME_CYCLE.indexOf(themePreference) + 1) % THEME_CYCLE.length]
    setThemePreference(next)
  }
  const ThemeIcon = THEME_ICONS[themePreference]

  // Same query keys as TransactionsPage — TanStack Query dedupes/shares the cache, so
  // opening this modal from another page doesn't trigger a second network round-trip
  // if the Transactions page happens to be mounted too.
  const { data: accountsList } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccountsApi(),
  })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })

  async function handleSignOut() {
    try {
      await signOut()
    } catch {
      // best-effort audit record
    }
    await signOutAndReset(clerk, ROUTES.AUTH.SIGN_IN)
  }

  const avatarPreset = user?.avatarPreset ?? 'avatar-coral'
  const displayName = user?.displayName ?? user?.email ?? ''

  return (
    <div className="flex h-screen bg-[var(--fl-bg)] overflow-hidden">
      <SampleWatermark />
      {/* ── Desktop / tablet sidebar ── */}
      <aside
        className={[
          'hidden lg:flex flex-col shrink-0 bg-[var(--fl-surface-subtle)] transition-all duration-200',
          sidebarExpanded ? 'w-60' : 'w-16',
        ].join(' ')}
      >
        {/* Brand header */}
        <div className="px-4 py-6 mb-2">
          {sidebarExpanded ? (
            <div className="flex items-center gap-3">
              <LogoMark size="md" />
              <h1 className="font-bold text-[var(--fl-primary)] leading-tight text-base">
                Log My Spends
              </h1>
            </div>
          ) : (
            <LogoMark size="md" className="mx-auto" />
          )}
        </div>

        {/* Add Transaction — top billing, since it's the app's single most frequent action.
            Hidden whenever the active household is read-only — VIEWER role, or the household
            itself archived (enforced server-side too — see HouseholdGateFilter) — since the
            button would just 403 for them. */}
        {!isReadOnly &&
          (sidebarExpanded ? (
            <div className="px-2 pb-3">
              <Button
                onClick={() => setAddTransactionOpen(true)}
                className="h-auto w-full gap-2 rounded-lg py-3 font-semibold active:scale-95"
              >
                <Plus size={16} />
                Add Transaction
              </Button>
            </div>
          ) : (
            <div className="px-2 pb-3">
              <Button
                onClick={() => setAddTransactionOpen(true)}
                title="Add Transaction"
                className="h-auto w-full rounded-lg py-3 active:scale-95"
              >
                <Plus size={16} />
              </Button>
            </div>
          ))}

        {/* Nav items */}
        <nav className="flex-1 px-2 space-y-0.5 pb-2">
          {NAV_ITEMS.map(({ label, to, Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={!sidebarExpanded ? label : undefined}
              className={({ isActive }) =>
                isActive
                  ? 'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors bg-[var(--fl-primary-subtle)] text-[var(--fl-income)]'
                  : 'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-[var(--fl-text-2)] hover:bg-[var(--fl-primary-subtle)] hover:text-[var(--fl-heading)]'
              }
            >
              <span className="relative shrink-0">
                <Icon size={18} />
                {!sidebarExpanded && to === ROUTES.APP.RECURRING && dueCount > 0 && (
                  <span
                    aria-label={`${dueCount} due`}
                    className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[var(--fl-warning-text)]"
                  />
                )}
              </span>
              {sidebarExpanded && <span className="flex-1">{label}</span>}
              {sidebarExpanded && to === ROUTES.APP.RECURRING && <NavBadge count={dueCount} />}
            </NavLink>
          ))}
        </nav>

        {/* Collapse + theme — both "chrome" controls (not navigation destinations), paired in
            one utility row rather than mixed into the nav list. Theme here cycles Light ->
            Dark -> System as a quick shortcut; the full 3-way control still lives in
            Settings > Profile. */}
        <div
          className={[
            'px-2 pb-2 flex gap-1',
            sidebarExpanded ? 'flex-row items-center justify-between' : 'flex-col items-center',
          ].join(' ')}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarExpanded((s) => !s)}
            tooltip={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            className="text-[var(--fl-text-2)]"
          >
            {sidebarExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            data-testid="sidebar-theme-toggle"
            onClick={cycleTheme}
            aria-label={`Theme: ${THEME_LABELS[themePreference]}. Click to change.`}
            tooltip={`Theme: ${THEME_LABELS[themePreference]}`}
            className="text-[var(--fl-text-2)]"
          >
            <ThemeIcon size={16} />
          </Button>
        </div>

        {/* Account area — identity and the active household combined into one control (see
            AccountMenu) rather than a separate profile button and switcher. */}
        <div className="p-3 border-t border-[var(--fl-border)] space-y-1.5">
          {sidebarExpanded && <ReadOnlyBadge />}
          <AccountMenu
            displayName={displayName}
            avatarPreset={avatarPreset}
            collapsed={!sidebarExpanded}
            onProfileClick={() => navigate(ROUTES.APP.SETTINGS_PROFILE)}
            onSignOut={handleSignOut}
          />
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Phone top bar: brand and profile only — navigation and Add live in the tab bar. */}
        <header className="lg:hidden flex items-center justify-between px-4 py-2.5 bg-[var(--fl-surface)] border-b border-[var(--fl-border)] shadow-sm">
          <div className="flex items-center gap-2">
            <LogoMark size="sm" />
            <p className="font-semibold text-[var(--fl-primary)]">Log My Spends</p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setMenuOpen(true)}
            tooltip="Account and menu"
            className="h-auto w-auto rounded-full p-0"
          >
            <Avatar avatarPreset={avatarPreset} size="sm" />
          </Button>
        </header>
        <PullToRefreshIndicator pull={pull} refreshing={refreshing} />

        {/* Page content */}
        <main
          ref={mainRef}
          className="flex flex-1 flex-col overflow-auto overscroll-y-contain pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0"
        >
          {/* Padding, not a transform, moves the page down: a transform would re-anchor the
           *  pages' position:fixed bars to this wrapper mid-pull. */}
          <div
            className={cn(
              'flex flex-1 flex-col',
              !pull && 'transition-[padding,opacity] duration-200',
              refreshing && 'opacity-45'
            )}
            style={{ paddingTop: pull || (refreshing ? REFRESHING_GAP : undefined) }}
          >
            {entitlement?.status === 'trialing' && (
              <TrialBanner trialEndsAt={entitlement.trialEndsAt} />
            )}
            {entitlement && !entitlement.entitled && <TrialEndedBanner />}
            <Outlet />
          </div>
        </main>
      </div>

      <MobileTabBar
        onAdd={isReadOnly ? undefined : () => setAddTransactionOpen(true)}
        onMenu={() => setMenuOpen(true)}
        menuOpen={menuOpen}
        menuDot={dueCount > 0}
      />
      {menuOpen && (
        <MobileMenuSheet
          onClose={() => setMenuOpen(false)}
          badges={{ [ROUTES.APP.RECURRING]: dueCount }}
          footer={
            <div className="space-y-1.5">
              <ReadOnlyBadge />
              <Button
                variant="ghost"
                onClick={cycleTheme}
                aria-label={`Theme: ${THEME_LABELS[themePreference]}. Click to change.`}
                className="h-9 w-full justify-start gap-2 px-2 text-sm font-medium text-[var(--fl-text-2)]"
              >
                <ThemeIcon size={16} aria-hidden="true" /> Theme: {THEME_LABELS[themePreference]}
              </Button>
              <AccountMenu
                displayName={displayName}
                avatarPreset={avatarPreset}
                onProfileClick={() => {
                  setMenuOpen(false)
                  navigate(ROUTES.APP.SETTINGS_PROFILE)
                }}
                onSignOut={handleSignOut}
              />
            </div>
          }
        />
      )}

      {addTransactionOpen && (
        <AddTransactionModal
          accounts={accountsList?.accounts ?? []}
          categories={categoriesData?.categories ?? []}
          onClose={() => setAddTransactionOpen(false)}
        />
      )}
    </div>
  )
}
